import type { CanonicalDocument, Layer } from '@besbello/design-core';
import type { DiagnosticCode, Severity } from '../types.js';
import type { EffectiveZone, ValidationContext } from '../rules.js';
import { DTF_RULE, EMBROIDERY_RULE, SCREEN_PRINT_RULE, SUBLIMATION_RULE } from './techniques.js';

export * from './techniques.js';

/**
 * VECTORES COMPARTIDOS entre navegador y servidor.
 *
 * El test de paridad (rules-core/src/parity.test.ts para Node y
 * apps/web/e2e/parity.spec.ts para el navegador) ejecuta ESTOS MISMOS casos en
 * los dos entornos y compara los diagnosticos codigo a codigo. Si divergen, el
 * build falla: es la unica garantia de que el aviso que ve el cliente en el
 * movil es el mismo que rechaza el servidor.
 */

export const NOW = '2026-08-09T10:00:00.000Z';

export const ZONE_PECHO: EffectiveZone = {
  zoneId: 'pecho-frontal',
  name: 'Pecho frontal',
  polygonMm: [
    { x: 0, y: 0 },
    { x: 280, y: 0 },
    { x: 280, y: 400 },
    { x: 0, y: 400 },
  ],
  allowedTechniques: ['dtf', 'screen_print', 'embroidery'],
  provenance: 'inherited',
  confidence: 0.9,
};

export const ZONE_TAZA: EffectiveZone = {
  zoneId: 'taza-360',
  name: 'Taza 360',
  polygonMm: [
    { x: 0, y: 0 },
    { x: 200, y: 0 },
    { x: 200, y: 85 },
    { x: 0, y: 85 },
  ],
  allowedTechniques: ['sublimation'],
  provenance: 'adjusted',
  confidence: 1,
};

const UUID = {
  image: '0b3f6c1e-7a2d-4e5f-9c11-2a3b4c5d6e7f',
  text: '1c4e7d2f-8b3e-4f60-ad22-3b4c5d6e7f80',
  shape: '2d5f8e30-9c4f-4071-be33-4c5d6e7f8091',
  extra: '3e60a941-ad50-4182-8f44-5d6e7f809102',
} as const;

const base = {
  z: 0,
  anchor: 'top-left' as const,
  opacity: 1,
  blend: 'normal' as const,
  locked: false,
};

export function imageLayer(over: Partial<Extract<Layer, { kind: 'image' }>> = {}): Layer {
  return {
    ...base,
    id: UUID.image,
    kind: 'image',
    transform: { a: 100, b: 0, c: 0, d: 75, e: 20, f: 20 },
    assetChecksum: `sha256:${'a'.repeat(64)}`,
    intrinsic: { widthPx: 1200, heightPx: 900, dpi: 300 },
    ...over,
  } as Layer;
}

export function textLayer(over: Partial<Extract<Layer, { kind: 'text' }>> = {}): Layer {
  return {
    ...base,
    id: UUID.text,
    kind: 'text',
    transform: { a: 1, b: 0, c: 0, d: 1, e: 20, f: 120 },
    text: 'BESBELLO',
    font: { family: 'Inter', version: '4.0', fileHash: 'sha256:aa11bb22', weight: 400, italic: false },
    sizeMm: 20,
    tracking: 0,
    lineHeightMm: 24,
    direction: 'ltr',
    align: 'left',
    textCase: 'none',
    color: { type: 'spot', book: 'Pantone Solid Coated', code: '300 C', preview: { r: 0, g: 94, b: 184 } },
    ...over,
  } as Layer;
}

export function shapeLayer(over: Partial<Extract<Layer, { kind: 'shape' }>> = {}): Layer {
  return {
    ...base,
    id: UUID.shape,
    kind: 'shape',
    transform: { a: 1, b: 0, c: 0, d: 1, e: 20, f: 200 },
    path: 'M 0 0 L 100 0 L 100 40 L 0 40 Z',
    fill: { type: 'spot', book: 'Pantone Solid Coated', code: '186 C', preview: { r: 200, g: 16, b: 46 } },
    ...over,
  } as Layer;
}

export function doc(layers: Layer[], zoneId = 'pecho-frontal', widthMm = 280, heightMm = 400): CanonicalDocument {
  return {
    schemaVersion: 3,
    origin: { zoneId, axis: 'y-down' },
    canvasMm: { width: widthMm, height: heightMm },
    layers,
  };
}

export interface ExpectedDiagnostic {
  code: DiagnosticCode;
  severity: Severity;
  layerId?: string;
}

export interface ValidationVector {
  name: string;
  document: CanonicalDocument;
  context: ValidationContext;
  /** Conjunto EXACTO de diagnosticos esperados (codigo + severidad + capa). */
  expected: ExpectedDiagnostic[];
}

const ctx = (over: Partial<ValidationContext>): ValidationContext => ({
  zone: ZONE_PECHO,
  technique: DTF_RULE,
  substrate: 'algodon',
  now: NOW,
  ...over,
});

export const VALIDATION_VECTORS: ValidationVector[] = [
  {
    name: 'dtf: diseno correcto, sin errores',
    document: doc([imageLayer(), shapeLayer()]),
    context: ctx({}),
    expected: [],
  },
  {
    name: 'dtf: RESOLUCION EFECTIVA insuficiente (la imagen se amplia a 280 mm)',
    document: doc([imageLayer({ transform: { a: 260, b: 0, c: 0, d: 195, e: 10, f: 10 } })]),
    context: ctx({}),
    expected: [{ code: 'EFFECTIVE_DPI_TOO_LOW', severity: 'error', layerId: UUID.image }],
  },
  {
    name: 'dtf: imagen ampliada respecto a su dpi declarado pero aun por encima del minimo',
    document: doc([imageLayer({ transform: { a: 150, b: 0, c: 0, d: 112.5, e: 10, f: 10 } })]),
    context: ctx({}),
    expected: [{ code: 'IMAGE_UPSCALED', severity: 'warning', layerId: UUID.image }],
  },
  {
    name: 'serigrafia: EXCESO DE TINTAS (cinco tintas planas para un maximo de cuatro)',
    document: doc([
      shapeLayer({ id: UUID.shape, fill: { type: 'spot', book: 'P', code: '1', preview: { r: 1, g: 1, b: 1 } } }),
      shapeLayer({ id: UUID.extra, fill: { type: 'spot', book: 'P', code: '2', preview: { r: 2, g: 2, b: 2 } }, stroke: { type: 'spot', book: 'P', code: '3', preview: { r: 3, g: 3, b: 3 } }, strokeWidthMm: 2 }),
      textLayer({ color: { type: 'spot', book: 'P', code: '4', preview: { r: 4, g: 4, b: 4 } } }),
      textLayer({ id: UUID.image, color: { type: 'spot', book: 'P', code: '5', preview: { r: 5, g: 5, b: 5 } }, transform: { a: 1, b: 0, c: 0, d: 1, e: 20, f: 260 } }),
    ]),
    context: ctx({ technique: SCREEN_PRINT_RULE }),
    expected: [
      { code: 'TOO_MANY_INKS', severity: 'error' },
      { code: 'GEOMETRY_ESTIMATED', severity: 'info', layerId: UUID.text },
      { code: 'GEOMETRY_ESTIMATED', severity: 'info', layerId: UUID.image },
      { code: 'TEXT_NOT_FROZEN', severity: 'info', layerId: UUID.text },
      { code: 'TEXT_NOT_FROZEN', severity: 'info', layerId: UUID.image },
    ],
  },
  {
    name: 'serigrafia: una imagen de tono continuo no es separable en tintas planas',
    document: doc([imageLayer()]),
    context: ctx({ technique: SCREEN_PRINT_RULE }),
    expected: [{ code: 'GRADIENT_NOT_ALLOWED', severity: 'error', layerId: UUID.image }],
  },
  {
    name: 'bordado: el CONTORNO DEL TEXTO tambien tiene grosor minimo',
    document: doc([
      textLayer({
        outline: { color: { type: 'spot', book: 'Madeira', code: '1000', preview: { r: 0, g: 0, b: 0 } }, widthMm: 0.3 },
      }),
    ]),
    context: ctx({ technique: EMBROIDERY_RULE }),
    expected: [
      { code: 'STROKE_TOO_THIN', severity: 'error', layerId: UUID.text },
      { code: 'GEOMETRY_ESTIMATED', severity: 'info', layerId: UUID.text },
      { code: 'TEXT_NOT_FROZEN', severity: 'info', layerId: UUID.text },
      { code: 'EXTERNAL_DIGITIZING_REQUIRED', severity: 'info' },
    ],
  },
  {
    name: 'un contorno de texto suficiente NO se avisa',
    document: doc([
      textLayer({
        outline: { color: { type: 'spot', book: 'Madeira', code: '1000', preview: { r: 0, g: 0, b: 0 } }, widthMm: 2 },
      }),
    ]),
    context: ctx({ technique: EMBROIDERY_RULE }),
    expected: [
      { code: 'GEOMETRY_ESTIMATED', severity: 'info', layerId: UUID.text },
      { code: 'TEXT_NOT_FROZEN', severity: 'info', layerId: UUID.text },
      { code: 'EXTERNAL_DIGITIZING_REQUIRED', severity: 'info' },
    ],
  },
  {
    name: 'ARCO que da mas de una vuelta: las letras se pisan',
    // 8 letras a 20 mm ocupan unos 88 mm de arco; con radio 5 mm eso son mas de
    // diecisiete radianes, casi tres vueltas.
    document: doc([textLayer({ arc: { radiusMm: 5, direction: 'up' } })]),
    context: ctx({}),
    expected: [
      { code: 'ARC_OVERLAP', severity: 'error', layerId: UUID.text },
      { code: 'GEOMETRY_ESTIMATED', severity: 'info', layerId: UUID.text },
      { code: 'TEXT_NOT_FROZEN', severity: 'info', layerId: UUID.text },
    ],
  },
  {
    name: 'un arco holgado NO se avisa',
    document: doc([textLayer({ arc: { radiusMm: 120, direction: 'down' } })]),
    context: ctx({}),
    expected: [
      { code: 'GEOMETRY_ESTIMATED', severity: 'info', layerId: UUID.text },
      { code: 'TEXT_NOT_FROZEN', severity: 'info', layerId: UUID.text },
    ],
  },
  {
    name: 'bordado: TRAZO DEMASIADO FINO',
    document: doc([
      shapeLayer({
        path: 'M 0 0 L 80 0 L 80 30 Z',
        fill: undefined,
        stroke: { type: 'spot', book: 'Madeira', code: '1000', preview: { r: 0, g: 0, b: 0 } },
        strokeWidthMm: 0.3,
      }),
    ]),
    context: ctx({ technique: EMBROIDERY_RULE }),
    expected: [
      { code: 'STROKE_TOO_THIN', severity: 'error', layerId: UUID.shape },
      { code: 'EXTERNAL_DIGITIZING_REQUIRED', severity: 'info' },
    ],
  },
  {
    name: 'sublimacion: SOPORTE NO ADMITIDO (algodon)',
    document: doc([imageLayer({ transform: { a: 100, b: 0, c: 0, d: 75, e: 10, f: 5 } })], 'taza-360', 200, 85),
    context: ctx({ technique: SUBLIMATION_RULE, zone: ZONE_TAZA, substrate: 'algodon' }),
    expected: [{ code: 'SUBSTRATE_NOT_SUPPORTED', severity: 'error' }],
  },
  {
    name: 'la capa se sale de la zona de marcaje',
    document: doc([imageLayer({ transform: { a: 100, b: 0, c: 0, d: 75, e: 240, f: 20 } })]),
    context: ctx({}),
    expected: [{ code: 'LAYER_OUTSIDE_ZONE', severity: 'error', layerId: UUID.image }],
  },
  {
    name: 'texto por debajo del cuerpo minimo del proceso',
    document: doc([textLayer({ sizeMm: 3, lineHeightMm: 4 })]),
    context: ctx({}),
    expected: [
      { code: 'TEXT_TOO_SMALL', severity: 'error', layerId: UUID.text },
      { code: 'GEOMETRY_ESTIMATED', severity: 'info', layerId: UUID.text },
      { code: 'TEXT_NOT_FROZEN', severity: 'info', layerId: UUID.text },
    ],
  },
  {
    name: 'blanco tecnico pedido a un proceso que no lo tiene',
    document: doc(
      [shapeLayer({ fill: { type: 'technical_white' }, transform: { a: 0.5, b: 0, c: 0, d: 0.5, e: 10, f: 10 } })],
      'taza-360',
      200,
      85,
    ),
    context: ctx({ technique: SUBLIMATION_RULE, zone: ZONE_TAZA, substrate: 'ceramica' }),
    expected: [
      { code: 'COLOR_TYPE_NOT_SUPPORTED', severity: 'error' },
      { code: 'TECHNICAL_WHITE_NOT_AVAILABLE', severity: 'error' },
    ],
  },
  {
    name: 'la tecnica no esta permitida en esa zona',
    document: doc([imageLayer({ transform: { a: 100, b: 0, c: 0, d: 40, e: 10, f: 10 } })], 'taza-360', 200, 85),
    context: ctx({ zone: ZONE_TAZA, technique: DTF_RULE, substrate: 'algodon' }),
    expected: [{ code: 'ZONE_TECHNIQUE_NOT_ALLOWED', severity: 'error' }],
  },
  {
    name: 'documento vacio',
    document: doc([]),
    context: ctx({}),
    expected: [{ code: 'EMPTY_DESIGN', severity: 'error' }],
  },
];
